import type { CancelToken } from "axios";
import type { IEmailCampaignDraft } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import type { EmailDocument } from "pages/secure/facility/marketing/emailBuilder/domain/types";
import { mapEmailBuilderValidationError } from "pages/secure/facility/marketing/emailBuilder/operations/mapEmailBuilderValidationError";
import type { TEmailBuilderValidationMessageDescriptor } from "pages/secure/facility/marketing/emailBuilder/operations/translateEmailBuilderValidationMessage";

export const EMAIL_BUILDER_AUTOSAVE_DEBOUNCE_MS = 5000;

export const EMAIL_BUILDER_AUTOSAVE_CONFLICT_LABEL = "Conflict";

export const EMAIL_BUILDER_AUTOSAVE_CONFLICT_MESSAGE =
  "This draft was changed elsewhere.";

export const EMAIL_BUILDER_AUTOSAVE_CONFLICT_RELOAD_WARNING =
  "Reloading will replace this draft with the latest saved version from the server. Any changes on this screen that haven't been saved will be lost.";

export const EMAIL_BUILDER_AUTOSAVE_ERROR_MESSAGE = "Couldn't save your draft. Try again.";

function isTechnicalValidationMessage(message: string): boolean {
  return (
    message.includes("sections[") ||
    message.startsWith("settings.") ||
    message.startsWith("schemaVersion")
  );
}

export function isEmailBuilderValidationApiMessage(message: string | null | undefined): boolean {
  if (typeof message !== "string") {
    return false;
  }

  const trimmed = message.trim();
  return trimmed.length > 0 && isTechnicalValidationMessage(trimmed);
}

export function emailBuilderAutosaveErrorMessage(
  status: number,
  apiMessage: string | null | undefined,
  document: EmailDocument | null = null,
): TEmailBuilderValidationMessageDescriptor | string {
  if (status === EMAIL_BUILDER_HTTP_CONFLICT) {
    return EMAIL_BUILDER_AUTOSAVE_CONFLICT_MESSAGE;
  }

  const trimmed = typeof apiMessage === "string" ? apiMessage.trim() : "";
  if (trimmed.length > 0) {
    return mapEmailBuilderValidationError(trimmed, document);
  }

  return EMAIL_BUILDER_AUTOSAVE_ERROR_MESSAGE;
}

export const EMAIL_BUILDER_HTTP_OK = 200;
export const EMAIL_BUILDER_HTTP_BAD_REQUEST = 400;
export const EMAIL_BUILDER_HTTP_CONFLICT = 409;

export interface IEmailBuilderAutosaveApiResponse<T> {
  status: number;
  data: T;
  message?: string | null;
}

export interface IEmailBuilderAutosaveSuccessMeta {
  revision: number;
  updatedAt: string | null;
}

export type TEmailBuilderAutosaveAttemptResult =
  | { kind: "ok"; draft: IEmailBuilderAutosaveSuccessMeta }
  | { kind: "conflict" }
  | { kind: "error"; status: number; message: string | null }
  | { kind: "cancelled" };

function draftUpdatedAt(draft: IEmailCampaignDraft): string | null {
  return typeof draft.updated_at === "string" ? draft.updated_at : null;
}

export function emailBuilderAutosaveSuccessMetaFromDraft(
  draft: IEmailCampaignDraft,
): IEmailBuilderAutosaveSuccessMeta | null {
  if (typeof draft.revision !== "number") {
    return null;
  }

  return {
    revision: draft.revision,
    updatedAt: draftUpdatedAt(draft),
  };
}

export async function attemptEmailBuilderAutosave(params: {
  putDraft: () => Promise<IEmailBuilderAutosaveApiResponse<IEmailCampaignDraft>>;
  cancelToken: CancelToken;
}): Promise<TEmailBuilderAutosaveAttemptResult> {
  const putRes = await params.putDraft();

  if (putRes.status === EMAIL_BUILDER_HTTP_OK && putRes.data != null) {
    const meta = emailBuilderAutosaveSuccessMetaFromDraft(putRes.data);
    if (meta === null) {
      return {
        kind: "error",
        status: EMAIL_BUILDER_HTTP_BAD_REQUEST,
        message: putRes.message ?? null,
      };
    }

    return { kind: "ok", draft: meta };
  }

  if (params.cancelToken.reason) {
    return { kind: "cancelled" };
  }

  if (putRes.status === EMAIL_BUILDER_HTTP_CONFLICT) {
    return { kind: "conflict" };
  }

  return { kind: "error", status: putRes.status, message: putRes.message ?? null };
}
